import { useController, ApplicationView } from '@aha-app/mvc';
import classNames from 'classnames';
import templatePlaceholder from 'images/template-placeholder.png';
import AhaAnalytics from 'javascripts/analytics';
import React, { useEffect, useMemo, useState } from 'react';
import Category from './Category';
import EmptyTemplateSearchResult from './EmptyTemplateSearchResult';
import styles from './PageTemplateModal.mod.less';
import SearchBox from './SearchBox';
import TemplateController, { TemplateCategories } from './TemplateController';
import { type NoteTemplate, type Project } from 'components/framework/models';
import DesktopNotesController from 'components/create_homepage/controllers/DesktopNotesController';
import bindHotkey, { unbindHotkey } from 'javascripts/keybindings/bindHotkey';
import { sortBy } from 'lodash';
import { humanize } from 'inflected';
import { type GlobalNoteTemplate } from './types';

const categoryOrder = [
  TemplateCategories.All,
  TemplateCategories.Workspace,
  TemplateCategories.Personal,
  TemplateCategories.Search,
];

function getShowNewTemplateButton(
  currentCategory: TemplateCategories,
  project: Project | null
) {
  if (currentCategory === TemplateCategories.Workspace) {
    return project?.projectPermissions?.createNoteTemplates ?? false;
  }

  if (currentCategory === TemplateCategories.Personal) {
    // Personal note templates are only available to Notebooks advanced plans
    return (
      !window.currentAccount?.onlyNotebooksFlavor ||
      window.currentAccount?.notebooksAdvanced
    );
  }

  return false;
}

function getShowCreateTemplateCTA(
  project: Project | undefined,
  customCategories: Array<TemplateCategories>
) {
  const {
    onlyKnowledgeFlavor,
    knowledgeAdvanced,
    onlyWhiteboardsFlavor,
    whiteboardsAdvanced,
  } = window.currentAccount;

  // Never show the promo to standalone essentials users
  if (
    (onlyKnowledgeFlavor && !knowledgeAdvanced) ||
    (onlyWhiteboardsFlavor && !whiteboardsAdvanced)
  ) {
    return false;
  }

  const canCreateWorkspaceTemplate =
    project?.projectPermissions?.createNoteTemplates ?? false;
  // Personal note templates are only available to Notebooks advanced plans
  const canCreatePersonalTemplate =
    !window.currentAccount?.onlyNotebooksFlavor ||
    window.currentAccount?.notebooksAdvanced;

  if (project) {
    const hasWorkspaceTemplates = customCategories.includes(
      TemplateCategories.Workspace
    );
    return !hasWorkspaceTemplates && canCreateWorkspaceTemplate;
  }

  const hasPersonalTemplates = customCategories.includes(
    TemplateCategories.Personal
  );
  return !hasPersonalTemplates && canCreatePersonalTemplate;
}

const PageTemplatePicker = () => {
  const notesController = useController(DesktopNotesController);
  const controller = useController(TemplateController);
  const { projectId, project, templates, currentCategory, parentRecord } =
    controller.state;
  const [filterText, setFilterText] = useState('');
  const [searchBoxKey, setSearchBoxKey] = useState(1);
  const defaultTitle = 'All guided templates';
  let title = defaultTitle as string | undefined;
  const categories = [...controller.categories];
  const customCategories = sortBy([...controller.customCategories], category =>
    categoryOrder.indexOf(category)
  );

  let filteredTemplates: Array<NoteTemplate | GlobalNoteTemplate> = [];
  let selectedCategory = currentCategory;

  const canCreatePages = notesController
    ? notesController.state.canCreatePages
    : true;

  useEffect(() => {
    if (notesController) {
      bindHotkey('ESC', () => {
        notesController.actionHideTemplateModal();
      });
      return () => {
        unbindHotkey('ESC');
      };
    }
  }, [notesController]);

  const sortedTemplates = useMemo(() => {
    return templates.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [templates]);

  if (filterText.length > 0) {
    const lowerFilterText = filterText.toLowerCase();
    filteredTemplates = sortedTemplates.filter(template =>
      template.name?.toLowerCase().includes(lowerFilterText)
    );
    customCategories.push(TemplateCategories.Search);
    selectedCategory = TemplateCategories.Search;
    if (filteredTemplates.length === 0) {
      title = `No results for ${filterText}`;
    } else {
      const pluralize = filteredTemplates.length > 1 ? 'results' : 'result';
      title = `${filteredTemplates.length} ${pluralize} for ${filterText}`;
    }
  } else {
    filteredTemplates = sortedTemplates.filter(
      template =>
        currentCategory === TemplateCategories.All ||
        template.categories?.includes(currentCategory) ||
        (currentCategory === TemplateCategories.Personal &&
          'isPersonal' in template &&
          template.isPersonal)
    );
    title =
      currentCategory === TemplateCategories.All
        ? defaultTitle
        : currentCategory;
  }

  const onCategoryClick = category => {
    if (category !== TemplateCategories.Search) {
      setFilterText('');
      setSearchBoxKey(searchBoxKey + 1);
      controller.actionSetCategory(category);
    }
  };

  const showNewTemplateButton = getShowNewTemplateButton(
    currentCategory as TemplateCategories,
    project
  );

  const showCreateTemplateCTA = getShowCreateTemplateCTA(
    project,
    customCategories
  );

  // Show a try tag and promo link if the CTA would normally appear, and the
  // account doesn't have roadmaps, knowledge advanced, or whiteboards advanced
  const showCreateTemplatePromo =
    showCreateTemplateCTA &&
    window.currentAccount?.hasCrossProductPromotionsEnabled &&
    !(
      window.currentAccount?.roadmapFlavor ||
      window.currentAccount?.knowledgeAdvanced ||
      window.currentAccount?.whiteboardsAdvanced
    );

  const createPersonalTemplateUrl = '/settings/user_note_templates';
  const createWorkspaceTemplateUrl = project
    ? `/settings/projects/${project.referencePrefix}/note_templates`
    : null;

  return (
    <div className={styles.templateModal} data-skip-drawer-close>
      <div className={styles.leftSide}>
        <div className={styles.leftSideScrollable}>
          <div className={styles.searchPanel}>
            <SearchBox
              key={searchBoxKey}
              placeholder={'Search all templates'}
              onChange={value => setFilterText(value)}
            />
          </div>
          <div>
            {customCategories.map(category => (
              <Category
                key={category}
                category={category}
                selected={category === selectedCategory}
                onClick={category => onCategoryClick(category)}
              />
            ))}
          </div>
          {categories.length > 0 && (
            <>
              <div className={styles.heading}>Categories</div>
              {categories.map(category => (
                <Category
                  key={category}
                  category={category}
                  selected={category === selectedCategory}
                  onClick={category => onCategoryClick(category)}
                />
              ))}
            </>
          )}
        </div>
        {showCreateTemplateCTA && (
          <div
            className={styles.createTemplateCTA}
            style={{ cursor: showCreateTemplatePromo ? 'pointer' : 'default' }}
            onClick={e => {
              if (showCreateTemplatePromo) {
                e.preventDefault();
                e.stopPropagation();
                window.location.href =
                  '/suite/knowledge?upgrade_location=knowledge_custom_template';

                AhaAnalytics.track(
                  'Feature-7333230212709605344',
                  'User clicks promotion',
                  {
                    'Upgrade location':
                      'Knowledge template modal custom template',
                    'Promotion product': 'Knowledge',
                    'Originating product': window.currentAccount?.primaryFlavor,
                  }
                );
              }
            }}
          >
            <div className={styles.createTemplateCTATitle}>
              Custom template
              {showCreateTemplatePromo && (
                <aha-promo-try-pill
                  font-sizing='large'
                  pill-size='small'
                  upgrade-location='knowledge_custom_template'
                  style={{ marginLeft: 'var(--spacer-2)' }}
                />
              )}
            </div>
            <div className={styles.createTemplateCTADescription}>
              Create a {projectId ? 'workspace' : 'personal'} template from a
              blank document.
            </div>
            <a
              href={
                projectId
                  ? createWorkspaceTemplateUrl
                  : createPersonalTemplateUrl
              }
            >
              {!showCreateTemplatePromo && (
                <aha-button kind='secondary' size='small'>
                  <i className='fa-regular fa-plus' />
                  Create custom template
                </aha-button>
              )}
            </a>
          </div>
        )}
      </div>
      <div className={styles.rightSide}>
        <div className={styles.templateHeader}>
          <div className={styles.templateHeaderTitle}>
            {title}
            {showNewTemplateButton && (
              <a
                href={
                  currentCategory === TemplateCategories.Personal
                    ? createPersonalTemplateUrl
                    : createWorkspaceTemplateUrl
                }
              >
                <aha-button
                  kind='secondary'
                  size='small'
                  style={{
                    marginLeft: '16px',
                  }}
                >
                  <i className='fa-regular fa-plus' />
                  New
                </aha-button>
              </a>
            )}
          </div>
          <aha-icon
            class={styles.closeButton}
            icon='far fa-close'
            onClick={() => controller.actionAbortChooseTemplate()}
          />
        </div>
        <div className={styles.templatesPanel}>
          <div className={styles.templatesList}>
            {filteredTemplates.length > 0 &&
              filteredTemplates.map(template => {
                const templateLink = controller.linkForTemplate(template);
                const templateButton = (
                  <aha-button
                    style={{
                      width: '100%',
                      height: '32px',
                      cursor: 'pointer',
                    }}
                    data-skip-drawer-close
                    onClick={event => {
                      if (!canCreatePages) return;

                      event.stopPropagation();
                      event.preventDefault();

                      controller.actionPlaceTemplate(template);
                      AhaAnalytics.track(
                        'Feature-7013353833234815065',
                        'User selects template',
                        {
                          'Template type': humanize(template.documentType),
                          Location: 'Library',
                          'Template category': template.categories?.join(', '),
                          'Template name': template.name,
                          'CTA used': 'Use Template',
                          'Record type': parentRecord?.type ?? 'None',
                          'Template origin': controller.isGlobalTemplate(
                            template
                          )
                            ? 'Aha! Admin'
                            : template.categories?.includes(
                                TemplateCategories.Personal
                              )
                            ? 'Personal'
                            : 'Workspace',
                        }
                      );
                    }}
                    kind='primary'
                    disabled={canCreatePages ? null : true}
                  >
                    Use template <aha-icon icon='far fa-right-long' />
                  </aha-button>
                );

                const buttonContent = templateLink ? (
                  <a
                    href={templateLink}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  >
                    {templateButton}
                  </a>
                ) : (
                  templateButton
                );

                return (
                  <div
                    className={styles.templateTileContainer}
                    key={template.id}
                  >
                    <div className={styles.templateTile}>
                      <div className={classNames(styles.templateTilePreview)}>
                        <img
                          className={
                            ('availableThumbnail' in template &&
                              template.availableThumbnail) ||
                            ''
                          }
                          src={
                            template.thumbnail ||
                            ('thumbnailUrl' in template &&
                              template.thumbnailUrl) ||
                            templatePlaceholder
                          }
                        />
                      </div>
                      <div className={classNames(styles.templateTileInfo)}>
                        <div
                          className={styles.templateTileCaption}
                          // variable name indicates HTML; setter is responsbile for safety
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: template.shortDescription?.htmlBody ?? '',
                          }}
                        />
                        <div>
                          {buttonContent}
                          {template.hasLongDescription && (
                            <aha-button
                              style={{
                                width: '100%',
                                height: '32px',
                                cursor: 'pointer',
                                color: 'var(--aha-blue-500)',
                                marginTop: '12px',
                              }}
                              onClick={() => {
                                controller.actionSetCurrentTemplate(template);
                                AhaAnalytics.track(
                                  'Feature-7013353833234815065',
                                  'User previews template',
                                  {
                                    'Template type': humanize(
                                      template.documentType
                                    ),
                                    'Template category':
                                      template.categories?.join(', '),
                                    'Template name': template.name,
                                    'CTA used': 'See details',
                                  }
                                );
                              }}
                            >
                              See details
                            </aha-button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.templateTileName}>
                      {template.name}
                    </div>
                  </div>
                );
              })}
          </div>
          {filterText.length > 0 && filteredTemplates.length === 0 && (
            <EmptyTemplateSearchResult />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationView(PageTemplatePicker);
